import { createClient } from '@supabase/supabase-js';
import {
  projects as apiProjects,
  layers as apiLayers,
  images as apiImages,
  paths as apiPaths,
  rules as apiRules,
  generations as apiGenerations,
  delegations as apiDelegations,
  userProfiles as apiUserProfiles,
  audit as apiAudit,
  notes as apiNotes,
  profile as apiProfile,
  pathGroups as apiPathGroups,
  generationJobs as apiGenerationJobs,
  curatedJobs as apiCuratedJobs,
  dashboardStats as apiDashboardStats,
  generationAnalytics as apiGenerationAnalytics,
} from './api';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create Supabase client
const client = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  }
});

// Intercept fetch to inject JWT on every request and handle JWT expiration
const originalFetch = client.rest.fetch;
client.rest.fetch = async function(url, options = {}) {
  const jwt = localStorage.getItem('boneforge_jwt');
  if (jwt) {
    // Properly merge headers - preserve all existing headers
    const existingHeaders = options.headers || {};
    options.headers = new Headers(existingHeaders);
    options.headers.set('Authorization', `Bearer ${jwt}`);
    options.headers.set('apikey', supabaseAnonKey);
  }
  
  try {
    const response = await originalFetch.call(this, url, options);
    
    // Check if response indicates JWT expiration
    if (response.status === 401 || response.status === 403) {
      const clonedResponse = response.clone();
      try {
        const errorData = await clonedResponse.json();
        const errorMessage = errorData.message || errorData.error || '';
        
        // Check for JWT expiration indicators
        if (errorMessage.includes('JWT') || errorMessage.includes('expired') || errorMessage.includes('token')) {
          console.log('🔑 JWT expired detected in response, clearing JWT and redirecting');
          localStorage.removeItem('boneforge_jwt');
          window.location.href = '/';
          return response;
        }
      } catch (e) {
        // If we can't parse the error, just return the original response
      }
    }
    
    return response;
  } catch (error) {
    // Check if the error message indicates JWT expiration
    if (error.message && (error.message.includes('JWT') || error.message.includes('expired') || error.message.includes('token'))) {
      console.log('🔑 JWT expired detected in error, clearing JWT and redirecting');
      localStorage.removeItem('boneforge_jwt');
      window.location.href = '/';
    }
    throw error;
  }
};

// Raw supabase client is NOT exported — all CRUD must go through db.* (which uses api.js).
// For realtime subscriptions, use realtimeClient from ./realtime.js instead.

// Helper to update JWT after authentication
export const setSupabaseJWT = (jwt) => {
  if (jwt) {
    localStorage.setItem('boneforge_jwt', jwt);
    console.log('✅ JWT stored in localStorage');
  } else {
    localStorage.removeItem('boneforge_jwt');
    console.log('✅ JWT removed from localStorage');
  }
};

// Helper to clear Supabase auth session
export const signOutSupabase = () => {
  return client.auth.signOut().catch(err => {
    console.error('[GraveForge Auth] Error clearing session:', err);
  });
};

// Database helper functions — thin wrapper delegating to API client (src/lib/api.js)
// This preserves the db.* interface used by components while routing through the server.

// Helper: extract wallet from localStorage JWT or return empty string
const _wallet = () => localStorage.getItem('boneforge_wallet') || '';

export const db = {
  // Projects
  async createProject(projectData) {
    const result = await apiProjects.create(projectData, _wallet());
    return result.data;
  },
  async getProjects(walletAddress) {
    const result = await apiProjects.list(walletAddress);
    return result.data;
  },
  async getProject(projectId) {
    const result = await apiProjects.getById(projectId, _wallet());
    return result.data;
  },

  // Layers
  async getLayers(projectId) {
    const result = await apiLayers.list(projectId, _wallet());
    return result.data;
  },
  async createLayer(layerData) {
    const result = await apiLayers.create(layerData, _wallet());
    return result.data;
  },
  async updateLayer(layerId, updates) {
    const result = await apiLayers.update(layerId, updates, _wallet());
    return result.data;
  },
  async deleteLayer(layerId) {
    await apiLayers.delete(layerId, _wallet());
  },

  // Images
  async createImage(imageData) {
    const result = await apiImages.create(imageData, _wallet());
    return result.data;
  },
  async updateImage(imageId, updates) {
    const result = await apiImages.update(imageId, updates, _wallet());
    return result.data;
  },
  async deleteImage(imageId) {
    await apiImages.delete(imageId, _wallet());
  },

  // Paths
  async createPath(pathData) {
    const result = await apiPaths.create(pathData, _wallet());
    return result.data;
  },
  async updatePath(pathId, updates) {
    const result = await apiPaths.update(pathId, updates, _wallet());
    return result.data;
  },
  async deletePath(pathId) {
    await apiPaths.delete(pathId, _wallet());
  },

  // Rules
  async createRule(ruleData) {
    const result = await apiRules.create(ruleData, _wallet());
    return result.data;
  },
  async updateRule(ruleId, updates) {
    const result = await apiRules.update(ruleId, updates, _wallet());
    return result.data;
  },
  async deleteRule(ruleId) {
    await apiRules.delete(ruleId, _wallet());
  },

  // Project Delegates (legacy)
  async inviteDelegate(projectId, delegateData) {
    const result = await apiDelegations.inviteDelegate(projectId, delegateData, _wallet());
    return result.data;
  },
  async getProjectDelegates(projectId) {
    const result = await apiDelegations.getProjectDelegates(projectId, _wallet());
    return result.data;
  },
  async updateDelegatePermissions(delegateId, permissions) {
    const result = await apiDelegations.updateDelegatePermissions(delegateId, permissions, _wallet());
    return result.data;
  },
  async respondToInvitation(delegateId, status) {
    const result = await apiDelegations.respondToInvitation(delegateId, status, _wallet());
    return result.data;
  },
  async revokeDelegate(delegateId) {
    const result = await apiDelegations.revokeDelegate(delegateId, _wallet());
    return result.data;
  },
  async getMyDelegations(walletAddress) {
    const result = await apiDelegations.getMyDelegations(walletAddress);
    return result.data;
  },

  // Layer Delegate Permissions
  async setLayerPermissions(layerId, delegateId, permissions) {
    const result = await apiDelegations.setLayerPermissions(layerId, delegateId, permissions, _wallet());
    return result.data;
  },
  async getLayerPermissions(layerId, delegateId) {
    const result = await apiDelegations.getLayerPermissions(layerId, delegateId, _wallet());
    return result.data;
  },

  async getProjectWithPermissions(projectId, walletAddress) {
    const result = await apiProjects.getWithPermissions(projectId, walletAddress);
    return result.data;
  },

  // Check if user has permission for specific action
  async checkPermission(projectId, walletAddress, action, layerId = null) {
    const projectData = await this.getProjectWithPermissions(projectId, walletAddress);

    if (projectData.userRole === 'creator') {
      return true;
    }

    const permissions = projectData.permissions;
    if (!permissions) return false;

    if (layerId) {
      const layerPerm = projectData.layerPermissions?.find(lp => lp.layer_id === layerId);
      if (layerPerm) {
        switch (action) {
          case 'view_layer': return layerPerm.can_view !== false;
          case 'edit_layer': return layerPerm.can_edit !== null ? layerPerm.can_edit : permissions.can_edit_layers;
          case 'delete_layer': return layerPerm.can_delete !== null ? layerPerm.can_delete : permissions.can_delete_layers;
          case 'edit_images': return layerPerm.can_edit_images !== null ? layerPerm.can_edit_images : permissions.can_edit_images;
          case 'delete_images': return layerPerm.can_delete_images !== null ? layerPerm.can_delete_images : permissions.can_delete_images;
        }
      }
    }

    switch (action) {
      case 'edit_layers': return permissions.can_edit_layers;
      case 'delete_layers': return permissions.can_delete_layers;
      case 'edit_images': return permissions.can_edit_images;
      case 'delete_images': return permissions.can_delete_images;
      case 'preview': return permissions.can_preview;
      case 'update_project_info': return permissions.can_update_project_info;
      default: return false;
    }
  },

  // Generations
  async createGeneration(generationData) {
    const result = await apiGenerations.create(generationData, _wallet());
    return result.data;
  },
  async getGenerations(projectId) {
    const result = await apiGenerations.list(projectId, _wallet());
    return result.data;
  },
  async deleteGeneration(generationId) {
    await apiGenerations.delete(generationId, _wallet());
  },
  async updateGeneration(generationId, updates) {
    const result = await apiGenerations.update(generationId, updates, _wallet());
    return result.data;
  },
  async toggleFavorite(generationId, projectId) {
    const result = await apiGenerations.toggleFavorite(generationId, projectId, _wallet());
    return result.data;
  },
  async getProjectFavorites(projectId) {
    const result = await apiGenerations.getFavorites(projectId, _wallet());
    return result.data;
  },

  // Archive Management
  async getInactiveProjects() {
    const result = await apiProjects.getInactive(_wallet());
    return result.data;
  },
  async archiveProject(projectId, reason = 'Inactive for configured period') {
    const result = await apiProjects.archive(projectId, reason, _wallet());
    return result.data;
  },
  async unarchiveProject(projectId) {
    const result = await apiProjects.unarchive(projectId, _wallet());
    return result.data;
  },
  async getProjectArchiveStatus(projectId) {
    const result = await apiProjects.getArchiveStatus(projectId, _wallet());
    return result.data;
  },
  async matchUploadedImage(fileHash, filenameHash, layerId) {
    const result = await apiImages.matchUploaded(fileHash, filenameHash, layerId, _wallet());
    return result.data;
  },
  async restoreArchivedImage(imageId, originalUrl, thumbnailUrl, previewUrl) {
    const result = await apiImages.restoreArchived(imageId, originalUrl, thumbnailUrl, previewUrl, _wallet());
    return result.data;
  },
  async getExpiredGenerations() {
    const result = await apiGenerations.getExpired(_wallet());
    return result.data;
  },
  async getArchiveSummary() {
    const result = await apiProjects.getArchiveSummary(_wallet());
    return result.data;
  },
  async updateProjectArchiveSettings(projectId, inactiveDaysBeforeArchive, autoArchive = true) {
    const result = await apiProjects.updateArchiveSettings(projectId, inactiveDaysBeforeArchive, autoArchive, _wallet());
    return result.data;
  },

  // Team Delegation
  async getProjectDelegations(projectId) {
    const result = await apiDelegations.list(projectId, _wallet());
    return result.data;
  },
  async createDelegation(projectId, ownerWallet, delegateWallet, role, notes = null) {
    const result = await apiDelegations.create(projectId, ownerWallet, delegateWallet, role, notes, _wallet());
    return result.data;
  },
  async createCustomDelegation(projectId, ownerWallet, delegateWallet, permissions, notes = null) {
    const result = await apiDelegations.createCustom(projectId, ownerWallet, delegateWallet, permissions, notes, _wallet());
    return result.data;
  },
  async revokeDelegation(delegationId) {
    const result = await apiDelegations.revoke(delegationId, _wallet());
    return result.data;
  },
  async updateDelegation(delegationId, updates) {
    const result = await apiDelegations.update(delegationId, updates, _wallet());
    return result.data;
  },
  async getAccessibleProjects(walletAddress) {
    const result = await apiProjects.getAccessible(walletAddress);
    return result.data;
  },
  async hasPermission(projectId, walletAddress, permission) {
    const result = await apiDelegations.hasPermission(projectId, walletAddress, permission);
    return result.data;
  },

  // User Profiles
  async getUserProfile(walletAddress) {
    try {
      const result = await apiUserProfiles.get(walletAddress);
      return result.data;
    } catch (e) {
      return null; // Not found
    }
  },
  async updateUserProfile(walletAddress, profile) {
    const result = await apiUserProfiles.upsert(walletAddress, profile);
    return result.data;
  },

  // Audit Log
  async logAuditEntry(projectId, userWallet, actionType, entityType, entityId, entityName, description, oldValues = null, newValues = null, metadata = null) {
    const result = await apiAudit.log({
      project_id: projectId,
      user_wallet: userWallet,
      action_type: actionType,
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
      description,
      old_values: oldValues,
      new_values: newValues,
      metadata
    }, userWallet);
    return result.data;
  },
  async getProjectActivity(projectId, limit = 50, offset = 0) {
    const result = await apiAudit.getProjectActivity(projectId, limit, offset, _wallet());
    return result.data;
  },
  async getUserActivity(userWallet, limit = 50) {
    const result = await apiAudit.getUserActivity(userWallet, limit);
    return result.data;
  },

  // Project Notes
  async getProjectNotes(projectId) {
    const result = await apiNotes.list(projectId, _wallet());
    return result.data;
  },
  async createProjectNote(projectId, userWallet, note) {
    const profile = await this.getUserProfile(userWallet);
    const displayName = profile?.display_name || userWallet.slice(0, 8) + '...';
    const result = await apiNotes.create(projectId, {
      user_wallet: userWallet,
      user_display_name: displayName,
      ...note
    }, userWallet);
    return result.data;
  },
  async updateProjectNote(noteId, updates) {
    const result = await apiNotes.update(noteId, updates, _wallet());
    return result.data;
  },
  async deleteProjectNote(noteId) {
    await apiNotes.delete(noteId, _wallet());
  },
  async createNoteReply(noteId, userWallet, content) {
    const profile = await this.getUserProfile(userWallet);
    const displayName = profile?.display_name || userWallet.slice(0, 8) + '...';
    const result = await apiNotes.createReply(noteId, content, userWallet);
    return result.data;
  },
  async getNoteReplies(noteId) {
    const result = await apiNotes.getReplies(noteId, _wallet());
    return result.data;
  },

  // Profile (forge_profile)
  async getProfile(walletAddress) {
    const result = await apiProfile.get(walletAddress || _wallet());
    return result.data;
  },
  async upsertProfile(walletAddress, profileData) {
    const result = await apiProfile.upsert(walletAddress || _wallet(), profileData);
    return result.data;
  },

  // Path Groups
  async getPathGroups(projectId) {
    const result = await apiPathGroups.list(projectId, _wallet());
    return result.data;
  },
  async createPathGroup(data) {
    const result = await apiPathGroups.create(data, _wallet());
    return result.data;
  },
  async updatePathGroup(id, updates) {
    const result = await apiPathGroups.update(id, updates, _wallet());
    return result.data;
  },
  async deletePathGroup(id) {
    await apiPathGroups.delete(id, _wallet());
  },

  // Paths list
  async getPaths(projectId) {
    const result = await apiPaths.list(projectId, _wallet());
    return result.data;
  },

  // Rules list
  async getRules(projectId) {
    const result = await apiRules.list(projectId, _wallet());
    return result.data;
  },

  // Dashboard Stats
  async getDashboardStats(walletAddress) {
    const result = await apiDashboardStats.get(walletAddress || _wallet());
    return result.data;
  },

  // Generation Jobs
  async getGenerationJobs(projectId, opts) {
    const result = await apiGenerationJobs.list(projectId, _wallet(), opts);
    return result.data;
  },
  async updateGenerationJob(id, updates) {
    const result = await apiGenerationJobs.update(id, updates, _wallet());
    return result.data;
  },
  async getGenerationJobStatus(id) {
    const result = await apiGenerationJobs.getStatus(id, _wallet());
    return result.data;
  },
  async getCuratedJobStatus(id) {
    const result = await apiCuratedJobs.getStatus(id, _wallet());
    return result.data;
  },

  // Generation Analytics
  async getGenerationAnalytics(projectId) {
    const result = await apiGenerationAnalytics.list(projectId, _wallet());
    return result.data;
  },

  // Delegations with projects
  async getDelegationsWithProjects(walletAddress) {
    const result = await apiDelegations.getWithProjects(walletAddress || _wallet());
    return result.data;
  },

  // Favorites count
  async getFavoritesCount(projectId) {
    const result = await apiGenerations.getFavoritesCount(projectId, _wallet());
    return result.data;
  },
};
